import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { money, formatDateTime } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, TrendingUp, TrendingDown, Trash2, User } from "lucide-react";
import SaldoProgressBar from "@/components/SaldoProgressBar";

const REASONS = [
  { value: "AUSENCIA", label: "Ausencia", defaultDir: "-" },
  { value: "PERMISO_SIN_GOCE", label: "Permiso sin goce", defaultDir: "-" },
  { value: "PERMISO_CON_GOCE", label: "Permiso con goce", defaultDir: "+" },
  { value: "DIA_FESTIVO", label: "Día festivo", defaultDir: "+" },
  { value: "HORAS_EXTRA", label: "Horas extra", defaultDir: "+" },
  { value: "OTRO", label: "Otro", defaultDir: "+" },
];

export default function AdjustmentsDialog({ employee, open, onOpenChange, onSaldoChanged }) {
  const [saldo, setSaldo] = useState(employee?.saldo || null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    direction: "+",
    amount: "",
    reason: "HORAS_EXTRA",
    notes: "",
  });

  const load = async () => {
    if (!employee?.id) return;
    setLoading(true);
    try {
      const [adjRes, empRes] = await Promise.all([
        api.get(`/employees/${employee.id}/adjustments`, { params: { scope: "month" } }),
        api.get(`/employees/${employee.id}`),
      ]);
      setLog(adjRes.data);
      setSaldo(empRes.data.saldo);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && employee) {
      setSaldo(employee.saldo);
      load();
      setShowForm(false);
      setForm({ direction: "+", amount: "", reason: "HORAS_EXTRA", notes: "" });
    }
  }, [open, employee?.id]);

  const onReasonChange = (v) => {
    const r = REASONS.find((x) => x.value === v);
    setForm((f) => ({ ...f, reason: v, direction: r?.defaultDir || f.direction }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const raw = Number(form.amount);
    if (!raw || raw <= 0) return toast.error("Ingresa un monto positivo");
    const signed = form.direction === "-" ? -raw : raw;
    try {
      const res = await api.post(`/employees/${employee.id}/adjustments`, {
        amount: signed,
        reason: form.reason,
        notes: form.notes,
      });
      toast.success("Ajuste registrado");
      setSaldo(res.data.saldo);
      setShowForm(false);
      setForm({ direction: "+", amount: "", reason: "HORAS_EXTRA", notes: "" });
      load();
      onSaldoChanged && onSaldoChanged(employee.id, res.data.saldo);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (aid) => {
    if (!confirm("¿Eliminar este ajuste? El saldo devengado se recalculará.")) return;
    try {
      const res = await api.delete(`/employees/${employee.id}/adjustments/${aid}`);
      toast.success("Ajuste eliminado");
      setSaldo(res.data.saldo);
      load();
      onSaldoChanged && onSaldoChanged(employee.id, res.data.saldo);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white" data-testid="adjustments-dialog">
        <DialogHeader>
          <DialogTitle>Ajustes de saldo devengado</DialogTitle>
        </DialogHeader>

        {/* Employee + saldo summary */}
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-md p-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#1D9E75] text-white flex items-center justify-center font-semibold text-sm">
              {employee.full_name?.[0]}
            </div>
            <div className="flex-1">
              <div className="font-semibold text-[#0F172A]">{employee.full_name}</div>
              <div className="text-xs text-[#64748B] font-mono">{employee.curp}</div>
            </div>
          </div>
          {saldo && (
            <>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Stat label="Bruto devengado" value={money(saldo.gross_accrued)} />
                <Stat label="Ajustes período" value={money(saldo.adjustments_total || 0)} accent={
                  (saldo.adjustments_total || 0) > 0 ? "text-[#147D5C]" :
                  (saldo.adjustments_total || 0) < 0 ? "text-rose-600" : ""
                } />
                <Stat label="Adelantos vigentes" value={money(saldo.outstanding_advances)} accent="text-slate-600" />
                <Stat label="Disponible hoy" value={money(saldo.available)} accent="text-[#147D5C]" highlight />
              </div>
              <div className="mt-4">
                <SaldoProgressBar saldo={saldo} />
              </div>
            </>
          )}
        </div>

        {/* Form + List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[#0F172A]">Historial de ajustes del mes</h3>
            {!showForm && (
              <Button
                size="sm"
                data-testid="open-adjustment-form"
                onClick={() => setShowForm(true)}
                className="bg-[#1D9E75] hover:bg-[#147D5C]"
              >
                <Plus className="w-4 h-4 mr-1" />Registrar ajuste
              </Button>
            )}
          </div>

          {showForm && (
            <form onSubmit={submit} className="border border-[#E2E8F0] rounded-md p-4 space-y-3 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Tipo</Label>
                  <div className="flex gap-2" role="radiogroup">
                    <button
                      type="button"
                      data-testid="adj-dir-plus"
                      onClick={() => setForm({ ...form, direction: "+" })}
                      className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                        form.direction === "+"
                          ? "bg-[#D1FAE5] border-[#1D9E75] text-[#147D5C]"
                          : "border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <TrendingUp className="w-4 h-4" /> Aumenta
                    </button>
                    <button
                      type="button"
                      data-testid="adj-dir-minus"
                      onClick={() => setForm({ ...form, direction: "-" })}
                      className={`flex-1 px-3 py-2 rounded-md border text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${
                        form.direction === "-"
                          ? "bg-rose-50 border-rose-300 text-rose-700"
                          : "border-[#E2E8F0] text-[#64748B] hover:bg-[#F8FAFC]"
                      }`}
                    >
                      <TrendingDown className="w-4 h-4" /> Reduce
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Monto (MXN)</Label>
                  <Input
                    data-testid="adj-amount"
                    type="number" min="0.01" step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                    placeholder="500.00"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Razón</Label>
                  <Select value={form.reason} onValueChange={onReasonChange}>
                    <SelectTrigger data-testid="adj-reason"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#475569]">Notas (opcional)</Label>
                  <Input
                    data-testid="adj-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Ej: turno dominical"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t border-[#E2E8F0]">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                <Button type="submit" data-testid="adj-submit" className="bg-[#1D9E75] hover:bg-[#147D5C]">
                  Guardar ajuste
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="py-8 flex justify-center"><div className="nomi-spin" /></div>
          ) : log.length === 0 ? (
            <div className="text-center py-8 text-sm text-[#64748B] border border-dashed border-[#E2E8F0] rounded-md">
              Sin ajustes registrados este mes.
            </div>
          ) : (
            <div className="border border-[#E2E8F0] rounded-md overflow-hidden" data-testid="adjustments-log">
              <table className="w-full text-sm">
                <thead className="bg-[#F8FAFC] text-[11px] uppercase tracking-wider text-[#64748B]">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Fecha</th>
                    <th className="px-3 py-2 text-left font-medium">Razón</th>
                    <th className="px-3 py-2 text-right font-medium">Monto</th>
                    <th className="px-3 py-2 text-left font-medium">Registrado por</th>
                    <th className="px-3 py-2 text-right font-medium w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {log.map((a) => (
                    <tr key={a.id} className="border-t border-[#E2E8F0]">
                      <td className="px-3 py-2 text-xs text-[#64748B]">{formatDateTime(a.applied_at)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-[#0F172A]">{a.reason_label}</div>
                        {a.notes && <div className="text-xs text-[#64748B]">{a.notes}</div>}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${a.amount > 0 ? "text-[#147D5C]" : "text-rose-600"}`}>
                        {a.amount > 0 ? "+" : ""}{money(a.amount)}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#475569]">
                        <div className="flex items-center gap-1.5">
                          <User className="w-3 h-3 text-[#94A3B8]" />
                          <div>
                            <div>{a.created_by_name || "—"}</div>
                            <div className="text-[10px] text-[#94A3B8] uppercase tracking-wider">{a.created_by_role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm" variant="ghost"
                          data-testid={`delete-adj-${a.id}`}
                          onClick={() => remove(a.id)}
                          className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent = "", highlight = false }) {
  return (
    <div className={highlight ? "bg-white border border-[#1D9E75] rounded-md p-2.5" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">{label}</div>
      <div className={`text-base font-bold tabular-nums mt-1 ${accent || "text-[#0F172A]"}`}>{value}</div>
    </div>
  );
}
